import { Separator } from '@/components/ui/separator'
import ProTip from '../ProTip'

export default function SalesWorkflowsSection() {
  return (
    <>
      <p className="text-sm text-muted-foreground mb-3">
        Every deal follows a journey from first touch to signed contract. Understanding how Grackle
        CRM supports each stage of this lifecycle helps you move deals forward consistently and
        avoid losing momentum.
      </p>

      <section id="sw-lead-capture" aria-labelledby="sw-lead-capture-heading">
        <h3 id="sw-lead-capture-heading" className="font-semibold mb-2">
          1. Capturing Leads
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Leads enter your CRM from several sources:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Web forms</span> — Leads submitted through your website
            are automatically created via the WS Form webhook integration
          </li>
          <li>
            <span className="font-medium">Manual entry</span> — Create leads from trade shows,
            referrals, or cold outreach by clicking <span className="font-medium">New Lead</span>
          </li>
          <li>
            <span className="font-medium">Imported data</span> — Bulk-imported from spreadsheets or
            migrated from another CRM
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          Each lead captures the person's name, company, contact details, and source. The source
          tracking helps you understand which channels produce the best results.
        </p>
      </section>
      <Separator />

      <section id="sw-qualification" aria-labelledby="sw-qualification-heading">
        <h3 id="sw-qualification-heading" className="font-semibold mb-2">
          2. Qualifying Leads
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Not every lead is a good fit. Your goal is to quickly determine if a lead is worth
          pursuing:
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>Review the lead details — do they match your ideal customer profile?</li>
          <li>Make initial contact (call, email) and log the interaction as a task or note</li>
          <li>
            Update the lead <span className="font-medium">status</span> and{' '}
            <span className="font-medium">rating</span> to reflect their interest level
          </li>
          <li>
            Use the stage progress indicator to track where they are in your qualification process
          </li>
        </ol>
        <ProTip>
          <span className="font-medium">Qualify fast:</span> In high-volume sales, speed matters.
          Aim to make initial contact within 24 hours of receiving a new lead. Leads that go cold
          are much harder to revive.
        </ProTip>
      </section>
      <Separator />

      <section id="sw-conversion" aria-labelledby="sw-conversion-heading">
        <h3 id="sw-conversion-heading" className="font-semibold mb-2">
          3. Converting to Opportunities
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          When a lead is qualified and interested, it is time to convert:
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            Open the lead and click <span className="font-medium">Convert</span>
          </li>
          <li>
            The system creates an Account (the company), a Contact (the person), and an Opportunity
            (the deal) — all in one step
          </li>
          <li>
            Information from the lead is carried forward so you do not have to re-enter anything
          </li>
          <li>The original lead is marked as converted</li>
        </ol>
        <p className="text-sm text-muted-foreground mt-2">
          If an account already exists for that company, the duplicate detection system will flag it
          so you can link the new contact and opportunity to the existing account.
        </p>
      </section>
      <Separator />

      <section id="sw-pipeline" aria-labelledby="sw-pipeline-heading">
        <h3 id="sw-pipeline-heading" className="font-semibold mb-2">
          4. Working the Pipeline
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Now the deal is an opportunity, and your job is to move it forward through the pipeline
          stages:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>Schedule meetings and demos — create events linked to the opportunity</li>
          <li>
            Track stakeholders — add contact roles to the opportunity so everyone involved is
            documented
          </li>
          <li>Add line items — attach the products and services you are proposing</li>
          <li>Create quotes — send formal pricing proposals</li>
          <li>Update the stage, amount, and close date as things evolve</li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          Every update you make flows into your Dashboard and pipeline reports, giving your team
          real-time visibility into the forecast.
        </p>
      </section>
      <Separator />

      <section id="sw-closing" aria-labelledby="sw-closing-heading">
        <h3 id="sw-closing-heading" className="font-semibold mb-2">
          5. Closing the Deal
        </h3>
        <p className="text-sm text-muted-foreground mb-2">When the customer says yes:</p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            Move the opportunity to <span className="font-medium">Closed Won</span> (Admin or Super
            Admin only — an attachment is required)
          </li>
          <li>An order is automatically created and linked to the opportunity</li>
          <li>Update the amount and close date to reflect the actual figures</li>
        </ol>
        <p className="text-sm text-muted-foreground mt-2">
          If the deal does not close, mark it as <span className="font-medium">Closed Lost</span>{' '}
          and add a note explaining why. This data feeds into your Win/Loss Analysis report and
          helps the team learn from every outcome.
        </p>
      </section>
      <Separator />

      <section id="sw-post-close" aria-labelledby="sw-post-close-heading">
        <h3 id="sw-post-close-heading" className="font-semibold mb-2">
          6. Post-Close Follow-Up
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Closing the deal is not the end — it is the start of the customer relationship:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>Create a follow-up task to check in after delivery or onboarding</li>
          <li>Keep the account record updated with any new contacts or expansion opportunities</li>
          <li>Use the activity timeline to maintain a record of ongoing interactions</li>
        </ul>
        <ProTip>
          <span className="font-medium">Expand and renew:</span> Your best leads are your existing
          customers. Set quarterly check-in tasks on your top accounts to uncover new opportunities
          and build long-term partnerships.
        </ProTip>
      </section>
      <Separator />

      <section id="sw-renewals" aria-labelledby="sw-renewals-heading">
        <h3 id="sw-renewals-heading" className="font-semibold mb-2">
          7. Renewal Opportunities
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          When a closed opportunity has a contract end date, the system automatically creates a
          renewal opportunity so your team can start the re-engagement process at the right time.
          The renewal opportunity is pre-populated with the original deal details and linked to the
          same account.
        </p>
        <p className="text-sm text-muted-foreground">
          Combined with{' '}
          <a
            href="#renewal-templates"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Renewal Task Templates
          </a>
          , this ensures that no contract renewal is missed — reminder tasks alert the owner at
          configurable intervals before the contract end date, and a fresh opportunity is ready for
          the next sales cycle.
        </p>
      </section>
    </>
  )
}
